import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { formatCurrency } from '../utils/format';

/**
 * ProviderLookup
 *
 * Look up any provider in the warehouse and see their lifetime activity
 * with the firm: total billed, total paid, total savings, avg reduction,
 * reduction percentile distribution, treatment-type breakdown, and the
 * most recent cases.
 *
 * Canonical-aware — reconciled variants roll up into one record. If a
 * name hasn't been reconciled yet (raw only), it's still searchable but
 * with a badge indicating the totals are for that raw variant only.
 */
function ProviderLookup() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null); // 'search' | 'detail' | 'unmerge' | 'merge'
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({}); // {name: true} for multi-select merge
  const [mergeDialog, setMergeDialog] = useState(null); // {names, canonical}
  const searchTimer = useRef(null);

  const toggleChecked = (name) => {
    setChecked((c) => {
      const next = { ...c };
      if (next[name]) delete next[name];
      else next[name] = true;
      return next;
    });
  };

  const checkedNames = Object.keys(checked);

  const openMergeDialog = () => {
    if (checkedNames.length < 2) return;
    // Default canonical: the checked name with the most tokens (most specific)
    const canonical = [...checkedNames].sort((a, b) => b.split(/\s+/).length - a.split(/\s+/).length)[0];
    setMergeDialog({ names: checkedNames, canonical });
  };

  const confirmMerge = async () => {
    if (!mergeDialog) return;
    setBusy('merge');
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/maylogic/providers/manual-merge`,
        { canonical_name: mergeDialog.canonical, raw_names: mergeDialog.names },
        { withCredentials: true }
      );
      alert(
        `Merged ${mergeDialog.names.length} providers into "${res.data.canonical_name}".\n` +
        `${res.data.aliases_created} new aliases · ${res.data.aliases_reassigned} reassigned · ` +
        `${res.data.provider_negotiations_backfilled.toLocaleString()} historical rows updated.`
      );
      setMergeDialog(null);
      setChecked({});
      if (query) runSearch(query);
    } catch (e) {
      setError('Merge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setBusy('search');
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/maylogic/provider-lookup?q=${encodeURIComponent(q)}&limit=30`,
        { withCredentials: true }
      );
      setResults(res.data.results || []);
    } catch (e) {
      setError('Search failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  }, []);

  // Debounced search-as-you-type
  const onQueryChange = (v) => {
    setQuery(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(v), 250);
  };

  const openDetail = async (name) => {
    setSelected(name);
    setDetail(null);
    setBusy('detail');
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/maylogic/provider-lookup/detail?name=${encodeURIComponent(name)}&recent_limit=50`,
        { withCredentials: true }
      );
      setDetail(res.data);
    } catch (e) {
      setError('Failed to load provider: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const clearDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const unmerge = async () => {
    if (!detail || !detail.provider_id) return;
    const n = detail.known_variants.length;
    const ok = window.confirm(
      `Un-merge "${detail.canonical_name}"?\n\n` +
      `This will split all ${n} raw name variants back into separate ` +
      `providers and clear the canonical from every historical case. ` +
      `Use when the clusterer wrongly grouped unrelated providers.`
    );
    if (!ok) return;
    setBusy('unmerge');
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/maylogic/providers/${detail.provider_id}/unmerge`,
        {},
        { withCredentials: true }
      );
      alert(
        `Un-merged "${res.data.canonical_name}".\n` +
        `${res.data.aliases_deleted} variants split apart · ` +
        `${res.data.provider_negotiations_cleared.toLocaleString()} historical rows updated.`
      );
      clearDetail();
      // Re-run search so results reflect the new state
      if (query) runSearch(query);
    } catch (e) {
      setError('Un-merge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const fmtPct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

  if (detail) {
    const d = detail.reduction_pct_distribution || {};
    // Bars are drawn relative to the largest percentile, so the shape of the
    // spread is legible even when every figure is small.
    const peak = Math.max(d.p25 || 0, d.p50 || 0, d.p75 || 0, d.p90 || 0, 0.01);
    const bars = [
      ['Conservative', 'p25', d.p25],
      ['Median', 'p50', d.p50],
      ['Aggressive', 'p75', d.p75],
      ['Ceiling', 'p90', d.p90],
    ];

    return (
      <>
        <button type="button" className="lm-link" onClick={clearDetail}>← Back to search</button>
        <div className="lm-eyebrow" style={{ marginTop: 14 }}>MayLogic · provider</div>

        <section className="lm-panel">
          <div className="lm-rowhead">
            <div className="lm-provider">
              <span className="lm-provider-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 20V9.5L12 4l8 5.5V20" strokeLinejoin="round" />
                  <path d="M9.5 12.5h5M12 10v5" strokeLinecap="round" />
                </svg>
              </span>
              <div>
                <h2>{detail.canonical_name}</h2>
                {detail.known_variants && detail.known_variants.length > 1 && (
                  <small className="lm-inline-muted" style={{ marginLeft: 0 }}>
                    {detail.known_variants.length} name variants combined
                  </small>
                )}
              </div>
            </div>
            <span className={`lm-tag${detail.source === 'raw' ? ' lm-warn' : ''}`}>
              {detail.source === 'raw' ? 'Un-reconciled' : 'Reconciled'}
            </span>
          </div>

          {detail.source === 'raw' && (
            <p className="lm-note">
              Totals below cover this exact spelling only. Other spellings of the
              same provider are counted separately until they are reconciled.
            </p>
          )}

          {detail.known_variants && detail.known_variants.length > 1 && (
            <p className="lm-note">
              <strong>Known variants:</strong> {detail.known_variants.join(' · ')}
            </p>
          )}

          <div className="lm-metrics" style={{ marginTop: 18 }}>
            <div className="lm-metric">
              <strong>{detail.totals.n_cases.toLocaleString()}</strong>
              <label>Completed cases</label>
              <small>Latest service {fmtDate(detail.totals.last_dos)}</small>
            </div>
            <div className="lm-metric">
              <strong>{formatCurrency(detail.totals.total_billed)}</strong>
              <label>Total billed</label>
              <small>Completed cases only</small>
            </div>
            {/* The one green figure on this page: the number that actually
                drives what you open a negotiation at. */}
            <div className="lm-metric lm-metric-accent">
              <strong>{fmtPct(detail.totals.avg_reduction_pct)}</strong>
              <label>Average reduction</label>
              <small>{detail.totals.n_cases.toLocaleString()} verified outcomes</small>
            </div>
          </div>

          {detail.source === 'canonical' && detail.known_variants && detail.known_variants.length > 1 && (
            <div className="lm-error" style={{ marginTop: 18 }}>
              <strong>Wrong merge?</strong> If this canonical wrongly groups unrelated
              providers (for example an 800-variant cluster from a bad clustering
              pass), un-merge splits every variant back apart.
              <div style={{ marginTop: 10 }}>
                <button type="button" className="lm-button" onClick={unmerge}
                        disabled={busy === 'unmerge'}>
                  {busy === 'unmerge' ? 'Un-merging…' : `Un-merge all ${detail.known_variants.length} variants`}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="lm-panel" style={{ marginTop: 14 }}>
          <div className="lm-two">
            <div>
              <h3>Historical reduction range</h3>
              <div className="lm-distribution">
                {bars.map(([label, key, value]) => (
                  <div className="lm-dist-col" key={key}>
                    <div className="lm-dist-bar"
                         style={{ '--h': `${Math.round(((value || 0) / peak) * 90) + 10}px` }} />
                    {label}
                    <b>{fmtPct(value)}</b>
                  </div>
                ))}
              </div>
              <p className="lm-note">
                Historical outcomes inform your judgment. They do not guarantee acceptance.
              </p>
            </div>
            <div>
              <h3>Payment history</h3>
              <div className="lm-kv"><span>Total paid</span><strong>{formatCurrency(detail.totals.total_paid)}</strong></div>
              <div className="lm-kv"><span>Verified savings</span><strong>{formatCurrency(detail.totals.total_savings)}</strong></div>
              <div className="lm-kv"><span>Latest date of service</span><strong>{fmtDate(detail.totals.last_dos)}</strong></div>
            </div>
          </div>
        </section>

        {detail.by_treatment_type && detail.by_treatment_type.length > 0 && (
          <section className="lm-panel" style={{ marginTop: 14 }}>
            <div className="lm-rowhead"><h2>By treatment type</h2></div>
            <div className="lm-tablewrap">
              <table>
                <thead>
                  <tr><th>Type</th><th className="lm-num">Cases</th><th className="lm-num">Total paid</th></tr>
                </thead>
                <tbody>
                  {detail.by_treatment_type.map((t, i) => (
                    <tr key={i}>
                      <td>{t.treatment_type}</td>
                      <td className="lm-num">{t.n_cases.toLocaleString()}</td>
                      <td className="lm-num">{formatCurrency(t.total_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="lm-panel" style={{ marginTop: 14 }}>
          <div className="lm-rowhead">
            <h2>Recent cases</h2>
            <span className="lm-inline-muted">Verified outcomes</span>
          </div>
          {detail.recent_cases.length === 0 ? (
            <div className="lm-empty">No cases found.</div>
          ) : (
            <div className="lm-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Variant used</th>
                    <th>Treatment</th>
                    <th className="lm-num">Billed</th>
                    <th className="lm-num">Paid</th>
                    <th className="lm-num">Reduction</th>
                    <th>DOS</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recent_cases.map((c, i) => (
                    <tr key={i}>
                      <td><strong>{c.client_name || '—'}</strong></td>
                      <td>{c.provider_name_raw}</td>
                      <td>{c.treatment_type || '—'}</td>
                      <td className="lm-num">{c.billed != null ? formatCurrency(c.billed) : '—'}</td>
                      <td className="lm-num">{c.paid != null ? formatCurrency(c.paid) : '—'}</td>
                      <td className="lm-num">{c.reduction_pct != null ? fmtPct(c.reduction_pct) : '—'}</td>
                      <td>{fmtDate(c.last_dos || c.first_dos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {error && <div className="lm-error" style={{ marginTop: 14 }}>{error}</div>}
      </>
    );
  }

  return (
    <>
      <div className="lm-eyebrow">MayLogic intelligence</div>
      <h1>Know the provider.<br />Shape the conversation.</h1>
      <p className="lm-subtitle">
        A clear view of the history behind your next negotiation — what they
        have billed, what the firm has paid, average reduction, and recent cases.
      </p>

      <div className="lm-toolbar">
        <label className="lm-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
               strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
          </svg>
          <input type="search" value={query}
                 onChange={(e) => onQueryChange(e.target.value)}
                 placeholder="Search by provider name (min 2 characters)…"
                 aria-label="Search providers by name" autoFocus />
        </label>
      </div>

      {busy === 'search' && <div className="lm-loading">Searching…</div>}
      {error && <div className="lm-error">{error}</div>}

      {query.length >= 2 && !busy && results.length === 0 && (
        <div className="lm-empty">No providers found matching "{query}".</div>
      )}

      {results.length > 0 && (
        <>
          {checkedNames.length >= 2 && (
            <div className="lm-attention">
              <p>
                <strong>{checkedNames.length} providers selected.</strong>
                <small>Merge them into one canonical provider?</small>
              </p>
              <button type="button" className="lm-button" onClick={() => setChecked({})}>Clear</button>
              <button type="button" className="lm-button lm-primary" onClick={openMergeDialog}>
                Merge selected →
              </button>
            </div>
          )}

          <section className="lm-panel" style={{ marginTop: 14 }}>
            <div className="lm-rowhead">
              <h2>Results<span className="lm-count">{results.length}</span></h2>
              <span className="lm-inline-muted">Tick two or more to merge</span>
            </div>
            <div className="lm-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th aria-label="Select" />
                    <th>Provider</th>
                    <th className="lm-num">Cases</th>
                    <th className="lm-num">Billed</th>
                    <th className="lm-num">Paid</th>
                    <th className="lm-num">Avg reduction</th>
                    <th>Latest DOS</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.provider_id || `raw:${r.name}:${i}`}>
                      <td>
                        <input type="checkbox" checked={!!checked[r.name]}
                               onChange={() => toggleChecked(r.name)}
                               aria-label={`Select ${r.name} to merge`} />
                      </td>
                      <td>
                        <strong>{r.name}</strong>
                        <small>
                          {r.canonical
                            ? `${r.known_variants.length} variants merged`
                            : 'Un-reconciled — this exact spelling only'}
                        </small>
                      </td>
                      <td className="lm-num">{r.n_cases.toLocaleString()}</td>
                      <td className="lm-num">{formatCurrency(r.total_billed)}</td>
                      <td className="lm-num">{formatCurrency(r.total_paid)}</td>
                      <td className="lm-num">{fmtPct(r.avg_reduction_pct)}</td>
                      <td>{fmtDate(r.last_dos)}</td>
                      <td className="lm-num">
                        <button type="button" className="lm-link" onClick={() => openDetail(r.name)}>
                          Open →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {mergeDialog && (
        <div className="lm-backdrop" onClick={() => setMergeDialog(null)}>
          <div className="lm-dialog" role="dialog" aria-modal="true"
               aria-label="Merge providers" onClick={(e) => e.stopPropagation()}>
            <h2>Merge {mergeDialog.names.length} providers</h2>
            <p className="lm-note">
              Set the canonical name. All selected variants will map to it, and
              their historical rows will aggregate under this one provider.
            </p>
            <label className="lm-field">
              Canonical name
              <input type="text" value={mergeDialog.canonical}
                     onChange={(e) => setMergeDialog({ ...mergeDialog, canonical: e.target.value })}
                     autoFocus />
            </label>
            <div className="lm-eyebrow">Merging these {mergeDialog.names.length} variants</div>
            <ul className="lm-varlist">
              {mergeDialog.names.map((n) => <li key={n}>{n}</li>)}
            </ul>
            <div className="lm-dialog-actions">
              <button type="button" className="lm-button" onClick={() => setMergeDialog(null)}>
                Cancel
              </button>
              <button type="button" className="lm-button lm-primary" onClick={confirmMerge}
                      disabled={busy === 'merge' || !mergeDialog.canonical.trim()}>
                {busy === 'merge' ? 'Merging…' : 'Confirm merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProviderLookup;
